// Building, reading and remembering share/invite links.
//
// The awkward constraint this module exists to solve: Google sign-in leaves
// and re-enters the app through a full page redirect, and the OAuth callback
// returns to `?auth=success&token=…` — a URL with our `?s=`/`?i=` parameter
// gone. AuthContext then strips even that with history.replaceState.
//
// So a link's code cannot live in the URL for as long as we need it. It is
// captured *once, synchronously, at startup* (see captureIncomingLink, called
// from index.js before React renders, so nothing can clear the query string
// first) and parked in localStorage. Whatever happens next — the visitor signs
// in, bounces through Google, comes back — the code is still there waiting.

import localStorageService from "../services/localStorageService";

export const SHARE_PARAMS = ["s", "share"];
export const INVITE_PARAMS = ["i", "invite"];
export const WATCH_PARAMS = ["w", "watch"];
export const JOIN_PARAMS = ["j", "join"];
export const TOGETHER_PARAMS = ["t", "together"];

const PENDING_KEY = "pending_link";

// Long enough to survive a Google round-trip on a slow phone, including a
// password prompt and 2FA. Short enough that a link followed last week does
// not ambush someone the next time they open the app.
const PENDING_TTL_MS = 60 * 60 * 1000;

/**
 * Absolute URL for a share code. Absolute because these are meant to be pasted
 * into a message to someone else, where a relative path is useless.
 */
export const buildShareUrl = (code, origin) => {
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/?s=${encodeURIComponent(code)}`;
};

export const buildInviteUrl = (code, origin) => {
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/?i=${encodeURIComponent(code)}`;
};

/**
 * Watch a live session read-only. The one link here that needs no account at
 * the far end, so it is also the one most likely to be forwarded on.
 */
export const buildWatchUrl = (code, origin) => {
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/?w=${encodeURIComponent(code)}`;
};

/**
 * Join a live session as a participant.
 *
 * Replaces the `/join/<code>` path the live lobby used to build, which nothing
 * ever routed: the app has no router, so that URL fell through to the SPA
 * fallback and the code was silently dropped. Anything sent to a friend before
 * this fix simply opened the app on their home screen.
 */
export const buildJoinUrl = (code, origin) => {
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/?j=${encodeURIComponent(code)}`;
};

/** Train a programme together (#184): join a mate's programme group. */
export const buildTogetherUrl = (code, origin) => {
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/?t=${encodeURIComponent(code)}`;
};

/**
 * Read one of our codes out of a query string.
 * Returns { type: "invite" | "join" | "together" | "watch" | "share", code } or null.
 *
 * Order is precedence, most specific first. An invite that points at a session
 * already carries the share and additionally connects the two people, so it
 * beats a bare share. A join beats a watch: someone holding both is a
 * participant who could merely watch, and taking part is the richer choice.
 */
export const parseLinkParams = (search) => {
  const params = new URLSearchParams(search || "");

  const groups = [
    ["invite", INVITE_PARAMS],
    ["join", JOIN_PARAMS],
    ["together", TOGETHER_PARAMS],
    ["watch", WATCH_PARAMS],
    ["share", SHARE_PARAMS],
  ];

  for (const [type, keys] of groups) {
    for (const key of keys) {
      const code = params.get(key);
      if (code) return { type, code };
    }
  }
  return null;
};

/**
 * Remove our own parameters from the address bar without disturbing anything
 * else on it. Called after capture so a refresh does not re-trigger the
 * landing dialog, and so the code is not left sitting in the URL to be
 * shoulder-surfed or pasted onward by accident.
 */
export const stripLinkParams = () => {
  if (typeof window === "undefined" || !window.history?.replaceState) return;

  const url = new URL(window.location.href);
  let touched = false;
  for (const key of [
    ...SHARE_PARAMS,
    ...INVITE_PARAMS,
    ...WATCH_PARAMS,
    ...JOIN_PARAMS,
    ...TOGETHER_PARAMS,
  ]) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      touched = true;
    }
  }
  if (!touched) return;

  const query = url.searchParams.toString();
  window.history.replaceState(
    {},
    "",
    `${url.pathname}${query ? `?${query}` : ""}${url.hash}`,
  );
};

export const setPendingLink = (link) => {
  if (!link?.code || !link?.type) return;
  localStorageService.set(PENDING_KEY, { ...link, at: Date.now() });
};

/**
 * The parked link, or null if there is none or it has gone stale.
 * Stale entries are cleared on read so they cannot resurface later.
 */
export const getPendingLink = () => {
  const stored = localStorageService.get(PENDING_KEY);
  if (!stored?.code || !stored?.type) return null;

  if (!stored.at || Date.now() - stored.at > PENDING_TTL_MS) {
    clearPendingLink();
    return null;
  }
  return { type: stored.type, code: stored.code };
};

export const clearPendingLink = () => {
  localStorageService.remove(PENDING_KEY);
};

/**
 * Startup hook: move any code in the URL into storage and clean the address
 * bar. Returns the captured link so a caller that runs before React can act on
 * it immediately.
 *
 * Idempotent — a second call with no parameters present leaves an
 * already-parked link untouched.
 */
/**
 * Rescue the old `/join/<code>` links.
 *
 * The live lobby used to build these, and nothing ever routed them — the SPA
 * fallback served index.html and the code was dropped on the floor. Links sent
 * before that was fixed are still sitting in people's chat histories, so read
 * the path as well as the query string and treat it as a join link.
 *
 * Path-only, and deliberately narrow: one segment, alphanumeric, the shape a
 * join code actually has.
 */
export const parseLegacyJoinPath = (pathname) => {
  const match = /^\/join\/([A-Za-z0-9]{4,16})\/?$/.exec(pathname || "");
  return match ? { type: "join", code: match[1].toUpperCase() } : null;
};

export const captureIncomingLink = () => {
  if (typeof window === "undefined") return getPendingLink();

  const queryLink = parseLinkParams(window.location.search);
  if (queryLink) {
    setPendingLink(queryLink);
    stripLinkParams();
    return queryLink;
  }

  const legacyLink = parseLegacyJoinPath(window.location.pathname);
  if (legacyLink) {
    setPendingLink(legacyLink);
    // Send the address bar back to the root, for the same reason the query
    // parameters are stripped: a refresh must not re-raise the dialog, and the
    // code should not sit on screen to be pasted onward.
    window.history?.replaceState?.({}, "", "/");
    return legacyLink;
  }

  return getPendingLink();
};

/**
 * Clipboard write with the execCommand fallback for browsers and contexts
 * where the async clipboard API is unavailable (notably non-HTTPS origins).
 * Returns true when the text made it across.
 */
export const copyTextToClipboard = async (text) => {
  if (!text) return false;

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      return true;
    } catch {
      return false;
    }
  }
};

/**
 * Hand a link to the OS share sheet where there is one, falling back to the
 * clipboard. Returns "shared", "copied" or "failed" so the caller can word the
 * confirmation honestly rather than claiming a copy that never happened.
 *
 * An abort (the user dismissing the sheet) is deliberately not a failure and
 * does not fall through to copying — they closed it on purpose.
 */
export const shareOrCopyLink = async ({ url, title, text }) => {
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ url, title, text });
      return "shared";
    } catch (error) {
      if (error?.name === "AbortError") return "shared";
      // Any other failure (no permission, unsupported payload) falls through
      // to the clipboard rather than leaving the user with nothing.
    }
  }
  return (await copyTextToClipboard(url)) ? "copied" : "failed";
};
